import { ChangeEvent, KeyboardEvent, useCallback, useState } from "react";

export interface UseMaskedInputArgs {
  /**
   * The mask that reflects the value from outside the input, so external updates (a picker, a
   * reset, a default) stay reflected in the text while the user is not editing.
   */
  value: string;
  /**
   * Formats raw input into the mask, dropping anything that cannot belong to it. Must be
   * idempotent — `mask(mask(x))` has to equal `mask(x)`, since masked text is re-masked on edit.
   */
  mask: (raw: string) => string;
  /**
   * Called when the input changes the masked value, with the new value and the one it replaced.
   * Fires even when the mask rejects the keystroke entirely, in which case the two are equal.
   */
  onMaskedChange?: (masked: string, previous: string) => void;
}

/**
 * Drives a text input whose content is constrained to a mask. Every keystroke is run through
 * `mask`, and whatever it rejects never enters state — so the input cannot hold text that does
 * not fit the format, rather than accepting it and validating after the fact.
 *
 * The masked string is the single source of truth; no separate raw value is kept, so the two
 * cannot drift. A lossy mask (one whose display characters are unrecoverable) would need a
 * different hook.
 *
 * @example
 * const { value, ...inputProps } = useMaskedInput({
 *   value: maskFromValue(date),
 *   mask: (raw) => maskDateInput(raw, { includeTime: true }),
 *   onMaskedChange: (masked, previous) => save(masked, previous),
 * })
 */
export const useMaskedInput = ({ value, mask, onMaskedChange }: UseMaskedInputArgs) => {
  const [editingValue, setEditingValue] = useState<string | null>(null);

  const maskedValue = editingValue ?? value;

  const apply = useCallback(
    (raw: string, previous: string) => {
      const masked = mask(raw);
      setEditingValue(masked);
      onMaskedChange?.(masked, previous);
    },
    [mask, onMaskedChange]
  );

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => apply(event.target.value, maskedValue),
    [apply, maskedValue]
  );

  // Backspacing onto a mask-owned separator would delete a character the mask immediately puts
  // back, trapping the caret. Drop everything up to the preceding content character instead, so
  // the deletion shortens the value rather than being undone.
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Backspace") return;

      const input = event.currentTarget;
      const caret = input.selectionStart ?? 0;
      const hasSelection = caret !== (input.selectionEnd ?? 0);
      const isSeparator = (char: string | undefined) => mask(char ?? "") === "";
      if (hasSelection || caret === 0 || !isSeparator(input.value[caret - 1])) return;

      event.preventDefault();
      let end = caret;
      while (end > 0 && isSeparator(input.value[end - 1])) end -= 1;
      apply(input.value.slice(0, Math.max(end - 1, 0)), maskedValue);
    },
    [apply, mask, maskedValue]
  );

  const onBlur = useCallback(() => setEditingValue(null), []);

  return { value: maskedValue, onChange, onKeyDown, onBlur };
};
